import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineCube, HiOutlineCloud, HiOutlineServer, HiOutlineSearch } from 'react-icons/hi';
import './Dashboard.css';

function Dashboard({ products, gcpProjects }) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const totalCloudRuns = products.reduce((sum, p) => sum + (p.cloudRuns?.length || 0), 0);

  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return products;
    const q = searchQuery.toLowerCase();
    return products.filter(p =>
      p.name.toLowerCase().includes(q) ||
      (p.cloudRuns || []).some(cr => cr.serviceName.toLowerCase().includes(q))
    );
  }, [products, searchQuery]);

  return (
    <div className="dashboard">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>GCP Cloud Run monitoring overview</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card" onClick={() => navigate('/gcp-projects')}>
          <div className="stat-icon blue">
            <HiOutlineCloud size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{gcpProjects.length}</span>
            <span className="stat-label">GCP Projects</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple">
            <HiOutlineCube size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{products.length}</span>
            <span className="stat-label">Products</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green">
            <HiOutlineServer size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{totalCloudRuns}</span>
            <span className="stat-label">Cloud Run Services</span>
          </div>
        </div>
      </div>

      <div className="dashboard-section">
        <div className="section-header-row">
          <h2>Products</h2>
          {products.length > 0 && (
            <div className="search-bar">
              <HiOutlineSearch size={16} />
              <input
                type="text"
                placeholder="Search products or services..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <span className="search-count">{filteredProducts.length} of {products.length}</span>
              )}
            </div>
          )}
        </div>
        {products.length === 0 ? (
          <div className="empty-state">
            <h3>No products yet</h3>
            <p>Add a product to start monitoring your Cloud Run services</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="empty-state">
            <h3>No matches</h3>
            <p>No products match "{searchQuery}"</p>
          </div>
        ) : (
          <div className="card-grid">
            {filteredProducts.map((product) => (
              <div
                key={product._id}
                className="card product-card"
                onClick={() => navigate(`/products/${product._id}`)}
              >
                <div className="product-card-header">
                  <h3>{product.name}</h3>
                  <span className="badge">{product.cloudRuns?.length || 0} services</span>
                </div>
                <div className="product-card-services">
                  {(product.cloudRuns || []).slice(0, 3).map((cr, i) => (
                    <div key={i} className="service-chip">
                      <span className="status-dot"></span>
                      {cr.serviceName}
                    </div>
                  ))}
                  {(product.cloudRuns || []).length > 3 && (
                    <span className="more-text">+{product.cloudRuns.length - 3} more</span>
                  )}
                </div>
                <div className="product-card-footer">
                  <span className="card-date">
                    Created {new Date(product.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
